import React, { useEffect, useState } from "react";
import { Form, Select, Button, message, Table } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { getUsers } from "../features/cutomers/customerSlice";
import { getStates } from "../features/statecity/statecitySlice";
import {
  getSchoolVisits,
  postAssignSchool,
} from "../features/attendance/attendanceSlice";

const AssignTarget = () => {
  const [selectedState, setSelectedState] = useState("");
  const [districtOptions, setDistrictOptions] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [vcData, setVcData] = useState([]);
  const [schoolData, setSchoolData] = useState([]);
  const [selectedVC, setSelectedVC] = useState(null);
  const [selectedSchoolRows, setSelectedSchoolRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stateName, setStateName] = useState("");

  const dispatch = useDispatch();
  const [form] = Form.useForm();

  const statelistState = useSelector(
    (state) => state.statecity.statelist || []
  );
  const vclistState = useSelector((state) => state.customer.customers || []);
  const schoolVisitsState = useSelector(
    (state) => state.attendance.schoolVisits || { success: false, schools: [] }
  );

  useEffect(() => {
    dispatch(getStates());
    dispatch(getUsers());
  }, [dispatch]);

  // Fetch School List based on State (and optionally District)
  useEffect(() => {
    if (selectedState) {
      setLoading(true);
      const payload = { state: selectedState };
      if (selectedDistrict) payload.district = selectedDistrict;

      dispatch(getSchoolVisits(payload))
        .then((response) => {
          console.log("API Response:", response.payload);

          if (response.payload.success && response.payload.schools.length > 0) {
            // Filter schools for selected state (and district if provided)
            const filteredSchools = response.payload.schools.filter(
              (school) =>
                school.state_code === selectedState &&
                (!selectedDistrict || school.district === selectedDistrict)
            );

            console.log("Filtered Schools:", filteredSchools);

            setSchoolData(
              filteredSchools.map((school) => ({
                key: school._id,
                schoolName: school.school_name,
                address: school.address || "N/A",
              }))
            );
          } else {
            setSchoolData([]);
            message.warning("No schools found for this selection.");
          }
        })
        .catch(() => message.error("Error fetching schools."))
        .finally(() => setLoading(false));
    }
  }, [selectedState, selectedDistrict, dispatch]);

  // Handle State Selection
  const handleStateChange = (value, option) => {
    setSelectedState(value);
    setSelectedDistrict("");
    setStateName(option.name);
    setSelectedVC(null);
    setSelectedSchoolRows([]);
    setSchoolData([]);

    // Update District Options
    const selectedStateObj = statelistState.find(
      (state) => state.code === value
    );
    setDistrictOptions(selectedStateObj?.district_name || []);

    // Fetch VC List for the selected state (no district filter)
    const filteredVCData = vclistState.filter(
      (customer) => customer.state === value && customer.role === "vc"
    );
    console.log("VC Data for State:", filteredVCData);
    setVcData(filteredVCData);
  };

  // Handle District Selection (optional)
  const handleDistrictChange = (value) => {
    setSelectedDistrict(value);
    setSelectedVC(null);
    setSelectedSchoolRows([]);
    // No need to filter VC data here; it’s already set by state
  };

  // Handle VC Selection
  const handleVcChange = (value) => {
    setSelectedVC(value);
  };

  // Table Row Selection
  const rowSelection = {
    selectedRowKeys: selectedSchoolRows,
    onChange: (selectedRowKeys) => {
      setSelectedSchoolRows(selectedRowKeys);
    },
  };

  // Assign Schools to VC
  const handleSubmit = async () => {
    if (!selectedVC || selectedSchoolRows.length === 0) {
      message.error("Please select a VC and at least one school.");
      return;
    }

    setLoading(true);
    try {
      const response = await dispatch(
        postAssignSchool({
          schoolId: selectedSchoolRows,
          vcId: selectedVC,
        })
      ).unwrap();

      message.success(
        `Successfully assigned ${selectedSchoolRows.length} schools to VC: ${
          response.message || "Success"
        }`
      );
      setSelectedVC(null);
      setSelectedSchoolRows([]);
      form.resetFields(["vc_name"]);
    } catch (error) {
      message.error(
        `Failed to assign schools: ${error.message || "Unknown error"}`
      );
    } finally {
      setLoading(false);
    }
  };

  // Reset Filters
  const resetFilters = () => {
    form.resetFields();
    setSelectedState("");
    setSelectedDistrict("");
    setSelectedVC(null);
    setSelectedSchoolRows([]);
    setSchoolData([]);
    setVcData([]);
    setStateName("");
  };

  // Table Columns
  const columns = [
    {
      title: "School Name",
      dataIndex: "schoolName",
      key: "schoolName",
    },
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
    },
  ];

  return (
    <div style={{ padding: "20px", fontFamily: "Arial, sans-serif" }}>
      <h4 style={{ fontSize: "large", fontWeight: "bold" }}>
        Assign Schools to VC
      </h4>

      <Form
        form={form}
        layout="inline"
        onFinish={handleSubmit}
        style={{ marginTop: "20px" }}
      >
        <Form.Item name="state_name">
          <Select
            placeholder="Select State"
            onChange={handleStateChange}
            style={{ width: 180 }}
            value={selectedState}
            allowClear
          >
            {statelistState.map((i) => (
              <Select.Option key={i.code} value={i.code} name={i.state_name}>
                {i.state_name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="district">
          <Select
            style={{ width: 180 }}
            placeholder="Select District"
            onChange={handleDistrictChange}
            loading={loading}
            value={selectedDistrict}
            allowClear
            disabled={!selectedState}
          >
            {districtOptions.map((districtName, idx) => (
              <Select.Option key={idx} value={districtName}>
                {districtName}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="vc_name">
          <Select
            placeholder="Select VC"
            onChange={handleVcChange}
            style={{ width: 180 }}
            value={selectedVC}
            disabled={!selectedState} // Enable VC dropdown as soon as state is selected
            allowClear
          >
            {vcData.map((item) => (
              <Select.Option key={item._id} value={item._id}>
                {item.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            disabled={!selectedVC || selectedSchoolRows.length === 0}
          >
            Assign Selected Schools
          </Button>
        </Form.Item>

        <Form.Item>
          <Button danger onClick={resetFilters}>
            Reset
          </Button>
        </Form.Item>
      </Form>

      {/* School List Table */}
      {selectedState && (
        <div style={{ marginTop: "20px" }}>
          <h5>
            Schools in {selectedDistrict || "All Districts"},{" "}
            {stateName || "Unknown State"}
          </h5>
          <Table
            rowSelection={rowSelection}
            columns={columns}
            dataSource={schoolData}
            loading={loading}
            pagination={{ pageSize: 10 }}
          />
        </div>
      )}
    </div>
  );
};

export default AssignTarget;
